import { hexlify } from '@ethersproject/bytes';
import { toUtf8Bytes } from '@ethersproject/strings';
import { serialize as serializeTransaction } from '@ethersproject/transactions';
import crypto from 'crypto';
import { Client, Constants, Utils } from 'gridplus-sdk';
import { WalletsError, WalletsErrorCode } from '../../types';
import { addHexPrefix, getFullPath, safeJSONParse, sanitizeTx, toChecksumAddress, keys, getConvertedPath, stripHexPrefix } from '../../utils';
import { wrapGridPlusError } from './errors';
import { HardwareWallet } from './hardware-wallet';
const POPUP_BASE_URL = 'https://lattice.gridplus.io';

const getPrivateKey = config => {
  const buf = Buffer.concat([Buffer.from(config.password), Buffer.from(config.deviceID), Buffer.from(config.name)]);
  return crypto.createHash('sha256').update(buf).digest();
};

const waitForPairing = config => {
  return new Promise((resolve, reject) => {
    const baseURL = POPUP_BASE_URL;
    const url = `${baseURL}?keyring=${config.name}`;
    const popup = window.open(url);

    if (popup === null) {
      throw new WalletsError('Popup blocked', WalletsErrorCode.HW_POPUP_BLOCKED);
    }

    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        reject(new WalletsError('Popup closed', WalletsErrorCode.CANCELLED));
      }
    }, 1000);
    popup.postMessage('GET_LATTICE_CREDS', baseURL);

    const receiveMessage = event => {
      if (event.origin !== baseURL) {
        return;
      }

      const [err, data] = safeJSONParse(event.data);

      if (err !== null) {
        return reject(err);
      }

      if ((data === null || data === void 0 ? void 0 : data.deviceID) === undefined || (data === null || data === void 0 ? void 0 : data.password) === undefined) {
        return reject(Error('Invalid credentials returned from Lattice.'));
      }

      return resolve(data);
    };

    window.addEventListener('message', receiveMessage, false);
  });
};

const getClient = async (config, client) => {
  var _client, _client2;

  if ((_client = client) !== null && _client !== void 0 && _client.isPaired && ((_client2 = client) === null || _client2 === void 0 ? void 0 : _client2.getActiveWallet()) !== null) {
    return {
      client,
      config
    };
  }

  const {
    deviceID,
    password,
    ...clientConfig
  } = config;

  if (client === undefined && deviceID !== undefined && password !== undefined) {
    const privKey = getPrivateKey(config);
    client = new Client({ ...clientConfig,
      privKey
    });
  }

  if (client && deviceID !== undefined && password !== undefined) {
    const isPaired = await client.connect(deviceID).catch(wrapGridPlusError);

    if (isPaired) {
      return {
        client,
        config
      };
    } else {
      await client.pair('').catch(() => null);
    }
  }

  const result = await waitForPairing(config);
  return getClient({ ...clientConfig,
    ...result
  }, client);
};

export class GridPlusWalletInstance {
  constructor(config, client, path, address) {
    this.config = config;
    this.client = client;
    this.path = path;
    this.address = address;
  }

  async getClient() {
    const {
      client,
      config
    } = await getClient(this.config, this.client);
    this.client = client;
    this.config = config;
    return this.client;
  }

  async buildSigningRequest(fwVersion, type, transaction) {
    if (fwVersion && (fwVersion.major > 0 || fwVersion.minor >= 15)) {
      const payload = Buffer.from(stripHexPrefix(serializeTransaction({ ...transaction,
        type
      })), 'hex');
      const callDataDecoder = transaction.to != null && transaction.data != null ? await Utils.fetchCalldataDecoder(transaction.data.toString(), transaction.to, transaction.chainId).catch(() => undefined) : undefined;
      return {
        data: {
          payload,
          curveType: Constants.SIGNING.CURVES.SECP256K1,
          hashType: Constants.SIGNING.HASHES.KECCAK256,
          encodingType: Constants.SIGNING.ENCODINGS.EVM,
          signerPath: getConvertedPath(this.path),
          decoder: callDataDecoder === null || callDataDecoder === void 0 ? void 0 : callDataDecoder.def
        }
      };
    }

    const {
      accessList,
      ...preHexTx
    } = transaction;
    const hexlified = keys(preHexTx).reduce((acc, cur) => {
      const value = preHexTx[cur];
      return value === undefined ? acc : { ...acc,
        [cur]: addHexPrefix(hexlify(value, {
          hexPad: 'left'
        }))
      };
    }, {});
    return {
      currency: 'ETH',
      data: { ...hexlified,
        ...(accessList ? {
          accessList
        } : {}),
        signerPath: getConvertedPath(this.path)
      }
    };
  }

  async signTransaction(rawTx) {
    const {
      type,
      ...transaction
    } = sanitizeTx(rawTx);

    if (transaction.chainId === undefined || transaction.nonce === undefined) {
      throw new WalletsError('Missing chainId or nonce on transaction', WalletsErrorCode.MISSING_ARGUMENTS);
    }

    const client = await this.getClient();
    const fwVersion = client.getFwVersion();
    const request = await this.buildSigningRequest(fwVersion, type, transaction);
    const result = await client.sign(request).catch(wrapGridPlusError);
    const signature = {
      v: result.sig.v.length === 0 ? 0 : parseInt(result.sig.v.toString('hex'), 16),
      r: addHexPrefix(result.sig.r.toString('hex')),
      s: addHexPrefix(result.sig.s.toString('hex'))
    };
    return serializeTransaction({ ...transaction,
      type
    }, signature);
  }

  async signMessage(message) {
    const bytes = toUtf8Bytes(message);
    const msgHex = hexlify(bytes);
    const data = {
      protocol: 'signPersonal',
      payload: msgHex,
      signerPath: getConvertedPath(this.path)
    };
    const client = await this.getClient();
    const result = await client.sign({
      currency: 'ETH_MSG',
      data
    }).catch(wrapGridPlusError);
    return addHexPrefix(result.sig.r.toString('hex') + result.sig.s.toString('hex') + result.sig.v.toString('hex'));
  }

  async getAddress() {
    if (!this.address) {
      const client = await this.getClient();
      const addresses = await client.getAddresses({
        startPath: getConvertedPath(this.path),
        n: 1
      }).catch(wrapGridPlusError);
      this.address = addresses[0];
    }

    return this.address;
  }

  async getPrivateKey() {
    throw new Error('Method not implemented.');
  }

}
export class GridPlusWallet extends HardwareWallet {
  constructor(config) {
    super();
    this.config = config;
  }

  async getClient() {
    const {
      client,
      config
    } = await getClient(this.config, this.client);
    this.client = client;
    this.config = config;
    return this.client;
  }

  async getAddress(path, index) {
    const wallet = await this.getWallet(path, index);
    return wallet.getAddress();
  }

  async getExtendedKey(_path) {
    throw new Error('Method not implemented.');
  }

  async getHardenedAddress(path, index) {
    return this.getAddress(path, index);
  }

  async getAddresses({
    path,
    limit,
    offset = 0,
    node
  }) {
    if (path.isHardened) {
      return super.getAddresses({
        path,
        limit,
        offset,
        node
      }).catch(wrapGridPlusError);
    }

    const client = await this.getClient();
    const dPath = getFullPath(path, offset);
    const addresses = await client.getAddresses({
      startPath: getConvertedPath(dPath),
      n: limit
    }).catch(wrapGridPlusError);
    return addresses.map((address, i) => {
      const index = offset + i;
      return {
        address: toChecksumAddress(address),
        index,
        dPath: getFullPath(path, index),
        dPathInfo: path
      };
    });
  }

  async getWallet(path, index, address) {
    const client = await this.getClient();
    return new GridPlusWalletInstance(this.config, client, getFullPath(path, index), address);
  }

  getCredentials() {
    return {
      deviceID: this.config.deviceID,
      password: this.config.password
    };
  }

}
//# sourceMappingURL=gridplus.js.map